import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSeo, FAVICON_URL } from '@/lib/seo';
import {
  useJsonLd,
  buildArticleSchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
} from '@/lib/schema';

const articleFaqs = [
  {
    q: 'What is an AllChinaBuy spreadsheet?',
    a: 'An AllChinaBuy spreadsheet is a shared, organized list of product finds, working links, QC photos and shipping notes. It groups items by category so buyers can browse shoes, hoodies, t-shirts, jackets, bags and more in one place instead of searching across many threads.',
  },
  {
    q: 'Is the AllChinaBuy spreadsheet free to use?',
    a: 'Yes. The spreadsheet and this guide are free informational resources. Final purchases always happen on the main website, where you can complete your order.',
  },
  {
    q: 'How often is the spreadsheet updated?',
    a: 'A good spreadsheet is updated regularly as new finds and working links are added. Check the last-updated note and prefer resources that stay current rather than old, stale sheets.',
  },
  {
    q: 'Can I buy directly from the spreadsheet?',
    a: 'No. The spreadsheet is a research and discovery tool. It links out to the main website, where you review product details and place your order securely.',
  },
  {
    q: 'How do I verify a product\u2019s QC before ordering?',
    a: 'Look for QC photos, measurements and seller notes in the listing. If a product has no QC data, ask the seller for photos before you commit to the order.',
  },
];

export default function Article() {
  const { t } = useTranslation();

  useSeo({
    title: 'AllChinaBuy Spreadsheet: Complete Guide to Finds, Links & QC',
    description:
      'A complete, independent guide to the AllChinaBuy spreadsheet — what it includes, how to use it, QC checks, shipping and warehouse tips, and how to browse shoes, hoodies, t-shirts and more safely.',
    keywords:
      'AllChinaBuy Spreadsheet, AllChinaBuy guide, AllChinaBuy finds, AllChinaBuy links, AllChinaBuy QC, AllChinaBuy shipping',
    path: '/allchinabuy-spreadsheet',
    type: 'article',
    image: FAVICON_URL,
  });

  useJsonLd([
    buildArticleSchema({
      title: 'AllChinaBuy Spreadsheet: Complete Guide to Finds, Links & QC',
      description:
        'A complete, independent guide to the AllChinaBuy spreadsheet, covering finds, links, QC checks, shipping and warehouse tips.',
      path: '/allchinabuy-spreadsheet',
      datePublished: '2026-01-01',
      dateModified: '2026-08-24',
    }),
    buildFaqSchema(articleFaqs),
    buildBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Shopping Guide', path: '/allchinabuy-spreadsheet' },
    ]),
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-24 pb-10">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">{t('nav.home')}</Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
            <span className="text-foreground-700">{t('nav.guide')}</span>
          </nav>

          <header className="mt-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-xs font-semibold text-primary-700">
              <i className="ri-book-open-line w-4 h-4 flex items-center justify-center" />
              {t('article.badge')}
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold leading-tight text-foreground-950">
              AllChinaBuy Spreadsheet: Complete Guide to Finds, Links & QC
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center" />
                {t('article.readTime')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center" />
                {t('article.updated')}: August 2026
              </span>
            </div>
          </header>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <p className="text-sm font-semibold text-foreground-900 mb-3">Table of Contents</p>
            <ul className="space-y-1.5 text-sm text-foreground-600">
              {[
                'What is the AllChinaBuy Spreadsheet?',
                'Why buyers use a spreadsheet instead of searching',
                'What a good spreadsheet includes',
                'How to use it step by step',
                'Categories you will find',
                'QC: how to check before you buy',
                'Shipping and warehouse tips',
                'How to avoid fake spreadsheets',
                'Spreadsheet vs traditional search',
                'Final thoughts',
              ].map((item, i) => (
                <li key={i}>
                  <a href={`#section-${i}`} className="hover:text-primary-700 cursor-pointer">
                    {i + 1}. {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <article className="mt-8 space-y-8 text-foreground-800 leading-relaxed">
            <p className="text-base md:text-lg text-foreground-700">
              If you spend any time buying through AllChinaBuy, you have probably heard the same advice
              repeated again and again: get the spreadsheet. It sounds simple, but the right spreadsheet
              can turn hours of scattered searching into a few minutes of focused browsing. This guide
              explains what the <strong>AllChinaBuy spreadsheet</strong> actually is, what sits inside a good
              one, and how to use it safely — from <Link to="/" className="text-primary-600 underline">our home page</Link> to
              your final order on the main website.
            </p>

            <section id="section-0">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">What is the AllChinaBuy Spreadsheet?</h2>
              <p className="mt-3 text-sm md:text-base">
                The AllChinaBuy spreadsheet is a curated, organized list of product finds, working links,
                QC photos and shipping notes. Rather than digging through forum posts, comment sections and
                chat rooms one at a time, buyers open a single sheet that groups items by category — shoes,
                hoodies, t-shirts, jackets, pants, bags, accessories and more — so every row shows the
                product, its link, a rough price range and its quality-control status.
              </p>
              <p className="mt-3 text-sm md:text-base">
                It is important to be clear about what this is and what it is not. This is an independent
                informational resource. We are not affiliated with or operated by AllChinaBuy, and the
                spreadsheet itself is not a shop. It is a discovery tool that points you toward products,
                while the actual purchase happens on the main website.
              </p>
            </section>

            <section id="section-1">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">Why buyers use a spreadsheet instead of searching</h2>
              <p className="mt-3 text-sm md:text-base">
                Searching one product at a time is slow. You repeat the same steps, open the same dead links
                and often end up looking at items that are already sold out or no longer stocked. A
                well-maintained spreadsheet removes most of that friction. Because everything is grouped by
                category and already checked, you can scan dozens of options in the time it would take to
                evaluate a single search result.
              </p>
              <p className="mt-3 text-sm md:text-base">
                The best sheets also carry the social proof of real use. Items that buyers keep linking back
                to tend to be the ones with decent QC photos and consistent sizing notes, which means you
                spend less time guessing and more time actually ordering.
              </p>
            </section>

            <section id="section-2">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">What a good spreadsheet includes</h2>
              <p className="mt-3 text-sm md:text-base">A useful AllChinaBuy spreadsheet usually contains most of the following:</p>
              <ul className="mt-3 space-y-2 text-sm md:text-base list-disc pl-5">
                <li>Product links that are still working and point to the main website.</li>
                <li>QC photos or references so you can see the item before committing.</li>
                <li>Category grouping, from shoes and hoodies to bags and accessories.</li>
                <li>Rough price ranges and, where available, sizing or seller notes.</li>
                <li>A last-updated date so you know the list is still current.</li>
              </ul>
              <p className="mt-3 text-sm md:text-base">
                If a spreadsheet has none of these — no dates, no QC, no working links — treat it as a weak
                resource and look for something better maintained.
              </p>
            </section>

            <section id="section-3">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">How to use it step by step</h2>
              <ol className="mt-3 space-y-3 text-sm md:text-base list-decimal pl-5">
                <li>Open the spreadsheet and pick a category from the dropdown or the grid.</li>
                <li>Scan the items and note the ones that match what you are looking for.</li>
                <li>Open the product link on the main website to check details and availability.</li>
                <li>Review any QC photos or measurements before you decide.</li>
                <li>Add the item to your order and complete the purchase on the main website.</li>
              </ol>
              <p className="mt-3 text-sm md:text-base">
                You can browse every category directly from <Link to="/" className="text-primary-600 underline">our homepage</Link>,
                which mirrors the same list of categories and links.
              </p>
            </section>

            <section id="section-4">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">Categories you will find</h2>
              <p className="mt-3 text-sm md:text-base">
                The spreadsheet covers the full range of what buyers look for, including shoes, hoodies and
                sweaters, t-shirts, jackets, pants and shorts, headwear, matching sets, underwear, jerseys,
                accessories, bags, belts, perfumes and other items. Each category links straight to the
                matching section on the main website so you never have to hunt for the right page.
              </p>
            </section>

            <section id="section-5">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">QC: how to check before you buy</h2>
              <p className="mt-3 text-sm md:text-base">
                Quality control is the step that protects your order. Before you commit to any item, look
                for QC photos, measurements and seller notes in the listing. Photos are the most important
                signal — they let you spot obvious flaws, color differences or sizing issues before your
                money is spent.
              </p>
              <p className="mt-3 text-sm md:text-base">
                If a listing has no QC data at all, slow down. Ask the seller for photos first, and do not
                treat a missing QC as a minor detail. A little patience at this stage saves a lot of
                frustration later.
              </p>
            </section>

            <section id="section-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">Shipping and warehouse tips</h2>
              <p className="mt-3 text-sm md:text-base">
                Shipping cost depends on the item, its weight and the shipping line you choose. One of the
                simplest ways to lower your cost per item is to combine several items into a single order
                instead of shipping them one at a time. Comparing similar products within the same category
                also helps, because lighter or smaller alternatives can meaningfully reduce the final
                shipping bill.
              </p>
            </section>

            <section id="section-7">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">How to avoid fake spreadsheets</h2>
              <p className="mt-3 text-sm md:text-base">
                Not every spreadsheet is trustworthy. Watch out for sheets that push shortened or third-party
                links, ask for login or payment details, or redirect you to unfamiliar pages. A legitimate
                resource links you to the main website and never asks you to enter credentials anywhere else.
              </p>
              <p className="mt-3 text-sm md:text-base">
                As a rule, only use links that come from the main website. If something feels off, close the
                tab and come back through the official route. Safety always comes first.
              </p>
            </section>

            <section id="section-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">Spreadsheet vs traditional search</h2>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-background-100 text-left">
                      <th className="border border-background-200 px-4 py-3 font-semibold text-foreground-900">Feature</th>
                      <th className="border border-background-200 px-4 py-3 font-semibold text-foreground-900">AllChinaBuy Spreadsheet</th>
                      <th className="border border-background-200 px-4 py-3 font-semibold text-foreground-900">Traditional Search</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Product discovery', 'Curated and organized', 'Scattered across threads'],
                      ['QC photos', 'Often included', 'Rarely available'],
                      ['Links', 'Grouped by category', 'Copied manually'],
                      ['Time to find an item', 'Minutes', 'Hours'],
                      ['Seller notes', 'Usually present', 'Often missing'],
                    ].map((row) => (
                      <tr key={row[0]} className="odd:bg-background-50">
                        <td className="border border-background-200 px-4 py-3 font-medium text-foreground-900">{row[0]}</td>
                        <td className="border border-background-200 px-4 py-3 text-foreground-700">{row[1]}</td>
                        <td className="border border-background-200 px-4 py-3 text-foreground-700">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="section-9">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">Final thoughts</h2>
              <p className="mt-3 text-sm md:text-base">
                A good AllChinaBuy spreadsheet is one of the simplest ways to save time and avoid bad
                purchases. Use it to discover products and check QC, then complete your order on the main
                website. Start by browsing the categories on <Link to="/" className="text-primary-600 underline">our homepage</Link>,
                and always double-check QC before you buy.
              </p>
              <p className="mt-4 text-sm text-foreground-500 italic">
                This is an independent informational resource and is not affiliated with or operated by
                AllChinaBuy.
              </p>
            </section>
          </article>

          <div className="mt-10 rounded-lg bg-primary-600 p-6 md:p-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">Ready to browse the latest finds?</h2>
            <p className="mt-2 text-sm text-background-100/90">
              Explore every category and jump straight to the products on the main website.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('hero.ctaPrimary')}
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('common.backHome')}
              </Link>
            </div>
          </div>

          <section className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {articleFaqs.map((f) => (
                <div key={f.q} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900">{f.q}</h3>
                  <p className="mt-2 text-sm text-foreground-600">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}